// src/components/modals/TestRunnerModal.tsx
// Test runner modal for proactive beta testing - guides users through tests step-by-step
import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext.tsx";
import { SurveyModal } from "./SurveyModal.tsx";
import "./TestRunnerModal.css";

// Test with step-by-step instructions
export interface TestWithSteps {
  testId: string;
  category: string;
  pageSection: string;
  itemType: string;
  description: string;
  expectedResult: string;
  steps: string[];
  navigateTo?: string; // URL to navigate to for the test
}

// Tests with detailed step-by-step instructions
const GUIDED_TESTS: TestWithSteps[] = [
  // Play Online Tests
  {
    testId: "P1-006",
    category: "Play Online",
    pageSection: "Color Picker",
    itemType: "Button",
    description: "Click light square color picker",
    expectedResult: "Color picker dialog opens",
    navigateTo: "/play-online",
    steps: [
      "Navigate to the Play Online page",
      "Look for the board color settings section",
      "Click on the light square color picker button",
      "Verify that a color picker dialog opens",
      "Select a new color and confirm it applies to light squares"
    ]
  },
  {
    testId: "P1-007",
    category: "Play Online",
    pageSection: "Color Picker",
    itemType: "Button",
    description: "Click dark square color picker",
    expectedResult: "Color picker dialog opens",
    navigateTo: "/play-online",
    steps: [
      "Navigate to the Play Online page",
      "Look for the board color settings section",
      "Click on the dark square color picker button",
      "Verify that a color picker dialog opens",
      "Select a new color and confirm it applies to dark squares"
    ]
  },
  {
    testId: "P2-004",
    category: "Play Online",
    pageSection: "Promotion Piece",
    itemType: "Button",
    description: "Click promotion piece",
    expectedResult: "Pawn promotes to selected piece",
    navigateTo: "/play-online",
    steps: [
      "Start a new game against the AI",
      "Advance a pawn to the opposite end of the board (8th rank for white, 1st rank for black)",
      "When the promotion dialog appears, click on the piece you want to promote to",
      "Verify the pawn is replaced with your selected piece",
      "Confirm the game continues normally with the new piece"
    ]
  },
  {
    testId: "P2-006",
    category: "Play Online",
    pageSection: "Button",
    itemType: "Button",
    description: "Click 'Claim Draw'",
    expectedResult: "Draw claimed game ends",
    navigateTo: "/play-online",
    steps: [
      "Start a game and play until the 50-move rule condition is met (50 moves without pawn move or capture)",
      "Look for the 'Claim Draw' button that should appear",
      "Click the 'Claim Draw' button",
      "Verify the game ends and is recorded as a draw",
      "Check that the game result shows correctly"
    ]
  },

  // Multiplayer Tests
  {
    testId: "MP1-011",
    category: "Multiplayer",
    pageSection: "Button",
    itemType: "Button",
    description: "Click 'Start Game' as host",
    expectedResult: "Game begins for all players",
    navigateTo: "/multiplayer",
    steps: [
      "Navigate to the Multiplayer page",
      "Create a new game room",
      "Wait for another player to join (or open a second browser/incognito window)",
      "As the host, click the 'Start Game' button",
      "Verify the game board appears for both players",
      "Confirm both players can see the initial position"
    ]
  },
  {
    testId: "MP1-014",
    category: "Multiplayer",
    pageSection: "Button",
    itemType: "Button",
    description: "Click 'Challenge' button on user",
    expectedResult: "Challenge sent to user",
    navigateTo: "/multiplayer",
    steps: [
      "Navigate to the Multiplayer page",
      "Look for online players in the lobby",
      "Click the 'Challenge' button next to a player's name",
      "Verify a challenge notification is sent",
      "Confirm the challenged player receives the challenge"
    ]
  },
  {
    testId: "FP-MP05",
    category: "Multiplayer Game",
    pageSection: "Failure Point",
    itemType: "Failure Point",
    description: "Move synchronization",
    expectedResult: "Moves sync in real-time",
    navigateTo: "/multiplayer",
    steps: [
      "Join or create a multiplayer game with two players",
      "As one player, make a move",
      "Check the other player's screen immediately",
      "Verify the move appears on both screens in real-time (within 1-2 seconds)",
      "Repeat with the other player making a move",
      "Confirm all moves sync correctly throughout the game"
    ]
  },

  // AI Game Tests
  {
    testId: "FP-AI05",
    category: "AI Game",
    pageSection: "Failure Point",
    itemType: "Failure Point",
    description: "AI move generation",
    expectedResult: "AI generates legal move in time",
    navigateTo: "/play-online",
    steps: [
      "Start a new game against the AI",
      "Make your first move as white",
      "Observe the AI thinking indicator",
      "Verify the AI responds with a move within the time limit",
      "Confirm the AI's move is a legal chess move",
      "Continue playing several moves to ensure consistency"
    ]
  },
  {
    testId: "FP-AI07",
    category: "AI Game",
    pageSection: "Failure Point",
    itemType: "Failure Point",
    description: "AI statistics display",
    expectedResult: "Thinking time nodes depth shown",
    navigateTo: "/play-online",
    steps: [
      "Start a new game against the AI",
      "Make a move and wait for the AI to respond",
      "Look for the AI statistics panel/display",
      "Verify you can see: thinking time, nodes searched, and search depth",
      "Confirm these values update with each AI move"
    ]
  },
  {
    testId: "FP-L07",
    category: "Local Game",
    pageSection: "Failure Point",
    itemType: "Failure Point",
    description: "Checkmate detection",
    expectedResult: "Game ends winner announced",
    navigateTo: "/play-online",
    steps: [
      "Start a new game (vs AI or set up a position)",
      "Play moves leading to checkmate (e.g., Scholar's Mate: 1.e4 e5 2.Bc4 Nc6 3.Qh5 Nf6 4.Qxf7#)",
      "Deliver checkmate to the opponent's king",
      "Verify the game immediately ends",
      "Confirm a message announces the winner",
      "Check that no further moves can be made"
    ]
  },

  // Special Moves Tests
  {
    testId: "FP-SM01",
    category: "Special Moves",
    pageSection: "Failure Point",
    itemType: "Failure Point",
    description: "Castling kingside",
    expectedResult: "King and rook move for O-O",
    navigateTo: "/play-online",
    steps: [
      "Start a new game",
      "Clear the squares between your king and kingside rook (move knight and bishop)",
      "Ensure the king has not moved, the rook has not moved, and you're not in check",
      "Click on your king, then click on the g1 (white) or g8 (black) square",
      "Verify both the king moves to g1/g8 AND the rook moves to f1/f8",
      "Confirm this happens in a single move"
    ]
  },
  {
    testId: "FP-SM02",
    category: "Special Moves",
    pageSection: "Failure Point",
    itemType: "Failure Point",
    description: "Castling queenside",
    expectedResult: "King and rook move for O-O-O",
    navigateTo: "/play-online",
    steps: [
      "Start a new game",
      "Clear the squares between your king and queenside rook (move queen, bishop, and knight)",
      "Ensure the king has not moved, the rook has not moved, and you're not in check",
      "Click on your king, then click on the c1 (white) or c8 (black) square",
      "Verify both the king moves to c1/c8 AND the rook moves to d1/d8",
      "Confirm this happens in a single move"
    ]
  },
  {
    testId: "FP-SM04",
    category: "Special Moves",
    pageSection: "Failure Point",
    itemType: "Failure Point",
    description: "En passant capture",
    expectedResult: "En passant captures correctly",
    navigateTo: "/play-online",
    steps: [
      "Start a new game and advance a pawn to the 5th rank (for white) or 4th rank (for black)",
      "Have the opponent move a pawn two squares from its starting position, landing next to your pawn",
      "On your very next move, capture diagonally behind the opponent's pawn",
      "Verify your pawn moves diagonally AND the opponent's pawn is removed",
      "Confirm this only works immediately after the opponent's two-square pawn move"
    ]
  },
  {
    testId: "FP-SM06",
    category: "Special Moves",
    pageSection: "Failure Point",
    itemType: "Failure Point",
    description: "Pawn promotion",
    expectedResult: "Pawn triggers promotion dialog",
    navigateTo: "/play-online",
    steps: [
      "Start a game and advance a pawn toward the opposite end",
      "Move the pawn to the 8th rank (white) or 1st rank (black)",
      "Verify a promotion dialog appears automatically",
      "Confirm you can see options for Queen, Rook, Bishop, and Knight",
      "Select a piece and verify the pawn is replaced"
    ]
  },

  // Draw Scenario Tests
  {
    testId: "FP-DR01",
    category: "Draw Scenarios",
    pageSection: "Failure Point",
    itemType: "Failure Point",
    description: "Stalemate detection",
    expectedResult: "Draw when no legal moves not in check",
    navigateTo: "/play-online",
    steps: [
      "Set up or play to a position where one player has no legal moves but is NOT in check",
      "Verify the game immediately declares a draw",
      "Confirm the result shows as 'Stalemate' or 'Draw by stalemate'",
      "Ensure no further moves can be made"
    ]
  },

  // Variant Creator Tests
  {
    testId: "VC2-003",
    category: "Variant Creator",
    pageSection: "Board Square",
    itemType: "Button",
    description: "Move piece on board",
    expectedResult: "Piece moves to drop location",
    navigateTo: "/variant-creator",
    steps: [
      "Navigate to the Variant Creator page",
      "Select a piece from the piece palette",
      "Click on a board square to place the piece",
      "Click and drag the piece to a different square",
      "Verify the piece moves to the new location",
      "Confirm the original square is now empty"
    ]
  },
  {
    testId: "FP-CV04",
    category: "Custom Variant",
    pageSection: "Failure Point",
    itemType: "Failure Point",
    description: "Non-standard board sizes",
    expectedResult: "4x4 to 12x12 boards work",
    navigateTo: "/variant-creator",
    steps: [
      "Navigate to the Variant Creator page",
      "Look for board size settings",
      "Change the board size to 10x10",
      "Verify the board displays correctly with 100 squares",
      "Try different sizes (6x6, 8x8, 12x12)",
      "Confirm pieces can be placed and the layout is correct"
    ]
  },

  // Clock Tests
  {
    testId: "FP-CL02",
    category: "Game with Clock",
    pageSection: "Failure Point",
    itemType: "Failure Point",
    description: "Clock countdown",
    expectedResult: "Clock counts down accurately",
    navigateTo: "/play-online",
    steps: [
      "Start a timed game (ensure clock is enabled in settings)",
      "Make a move and observe your opponent's clock",
      "Verify the clock counts down in real-time",
      "Compare with an external timer for accuracy",
      "Confirm the countdown is smooth without jumps"
    ]
  },
  {
    testId: "FP-CL05",
    category: "Game with Clock",
    pageSection: "Failure Point",
    itemType: "Failure Point",
    description: "Timeout detection",
    expectedResult: "Game ends when time expires",
    navigateTo: "/play-online",
    steps: [
      "Start a timed game with a short time control",
      "Let one player's clock run out completely",
      "Verify the game ends immediately when time reaches zero",
      "Confirm the correct player is declared the winner",
      "Check that the result shows 'Win on time' or similar"
    ]
  },

  // Edge Cases
  {
    testId: "EDGE-05",
    category: "Edge Cases",
    pageSection: "Test",
    itemType: "Test",
    description: "Rapid clicking",
    expectedResult: "Single action no duplicates",
    navigateTo: "/play-online",
    steps: [
      "Start a game and select a piece",
      "Rapidly click on a destination square multiple times",
      "Verify only ONE move is made, not multiple",
      "Try rapidly clicking buttons (like 'New Game')",
      "Confirm no duplicate actions occur"
    ]
  },
  {
    testId: "EDGE-07",
    category: "Edge Cases",
    pageSection: "Test",
    itemType: "Test",
    description: "Back button behavior",
    expectedResult: "Handled gracefully",
    navigateTo: "/play-online",
    steps: [
      "Start a game and make several moves",
      "Click the browser's back button",
      "Verify the app handles this gracefully (no crash, no errors)",
      "Check if you're warned about leaving the game",
      "Confirm navigation works correctly"
    ]
  },

  // AI Settings Tests
  {
    testId: "AI2-008",
    category: "AI Settings",
    pageSection: "Slider",
    itemType: "Slider",
    description: "Reset slider to default",
    expectedResult: "Value returns to default",
    navigateTo: "/ai-settings",
    steps: [
      "Navigate to the AI Settings page",
      "Change a slider value (like difficulty)",
      "Look for a 'Reset to Default' button",
      "Click the reset button",
      "Verify the slider returns to its default value",
      "Confirm all settings are reset if it's a global reset"
    ]
  },

  // User Profile Tests
  {
    testId: "UP1-008",
    category: "User Profile",
    pageSection: "Button",
    itemType: "Button",
    description: "Click 'Reactivate Account'",
    expectedResult: "Reactivation confirmation appears",
    navigateTo: "/profile",
    steps: [
      "Navigate to your Profile page",
      "If your account was previously deactivated, look for 'Reactivate Account'",
      "Click the 'Reactivate Account' button",
      "Verify a confirmation dialog appears",
      "Confirm the reactivation process works correctly"
    ]
  },

  // Chat Tests
  {
    testId: "CH2-001",
    category: "Chat Panel",
    pageSection: "Button",
    itemType: "Button",
    description: "Send chat message",
    expectedResult: "Message sent and displayed",
    navigateTo: "/multiplayer",
    steps: [
      "Join a multiplayer game with another player",
      "Find the chat input field",
      "Type a message and press Enter or click Send",
      "Verify your message appears in the chat",
      "Confirm the other player can see your message"
    ]
  },
];

// Get unique categories
const getCategories = (): string[] => {
  const categories = new Set(GUIDED_TESTS.map(t => t.category));
  return Array.from(categories).sort();
};

interface TestRunnerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ModalStep = 'select' | 'instructions' | 'running' | 'result' | 'complete';

export const TestRunnerModal: React.FC<TestRunnerModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();

  // State
  const [modalStep, setModalStep] = useState<ModalStep>('select');
  const [selectedTest, setSelectedTest] = useState<TestWithSteps | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [completedSteps, setCompletedSteps] = useState<boolean[]>([]);
  const [testResult, setTestResult] = useState<'pass' | 'fail' | null>(null);
  const [comments, setComments] = useState<string>("");
  const [startTime, setStartTime] = useState<number>(0);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>("");
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [isMinimized, setIsMinimized] = useState<boolean>(false);
  const [showSurvey, setShowSurvey] = useState<boolean>(false);
  const [lastReportId, setLastReportId] = useState<number | null>(null);

  // Reset when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setModalStep('select');
      setSelectedTest(null);
      setCategoryFilter("");
      setSearchQuery("");
      setCurrentStepIndex(0);
      setCompletedSteps([]);
      setTestResult(null);
      setComments("");
      setStartTime(0);
      setSubmitError("");
      setSubmitSuccess(false);
      setIsMinimized(false);
      setShowSurvey(false);
      setLastReportId(null);
    }
  }, [isOpen]);

  // Toggle minimize/maximize
  const handleMinimize = () => {
    setIsMinimized(true);
  };

  const handleMaximize = () => {
    setIsMinimized(false);
  };

  // Filter tests
  const filteredTests = GUIDED_TESTS.filter(test => {
    const matchesCategory = !categoryFilter || test.category === categoryFilter;
    const matchesSearch = !searchQuery ||
      test.testId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      test.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      test.pageSection.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Handle test selection
  const handleTestSelect = (test: TestWithSteps) => {
    setSelectedTest(test);
    setCompletedSteps(new Array(test.steps.length).fill(false));
    setModalStep('instructions');
  };

  // Start the test
  const handleStartTest = () => {
    setStartTime(Date.now());
    setCurrentStepIndex(0);
    setModalStep('running');
  };

  // Mark step as complete
  const handleStepComplete = (index: number) => {
    const newCompleted = [...completedSteps];
    newCompleted[index] = true;
    setCompletedSteps(newCompleted);

    // Move to next step if not the last
    if (index < (selectedTest?.steps.length || 0) - 1) {
      setCurrentStepIndex(index + 1);
    }
  };

  // Mark step as incomplete (toggle)
  const handleStepToggle = (index: number) => {
    const newCompleted = [...completedSteps];
    newCompleted[index] = !newCompleted[index];
    setCompletedSteps(newCompleted);
  };

  // Finish test and go to result
  const handleFinishTest = () => {
    setModalStep('result');
  };

  // Submit test result
  const handleSubmitResult = async () => {
    if (!selectedTest || !testResult) return;

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const duration = Math.round((Date.now() - startTime) / 1000);
      const stepsCompleted = completedSteps.filter(Boolean).length;

      const formData = new FormData();

      // Test info
      formData.append("test_id", selectedTest.testId);
      formData.append("category", selectedTest.category);
      formData.append("page_section", selectedTest.pageSection);
      formData.append("item_type", selectedTest.itemType);
      formData.append("description", selectedTest.description);
      formData.append("expected_result", selectedTest.expectedResult);

      // Test result
      formData.append("report_type", "test");
      formData.append("test_result", testResult);
      formData.append("test_steps_completed", stepsCompleted.toString());
      formData.append("test_duration_seconds", duration.toString());

      // Result details
      formData.append("actual_result", testResult === 'pass'
        ? `Test passed. ${stepsCompleted}/${selectedTest.steps.length} steps completed.`
        : `Test failed. ${stepsCompleted}/${selectedTest.steps.length} steps completed.`
      );
      formData.append("comments", comments);

      // Reporter info
      if (user?.userId) {
        formData.append("user_id", user.userId);
      }
      formData.append("reporter_name", user?.username || "");
      formData.append("reporter_email", user?.email || "");

      // Browser info
      formData.append("browser_info", navigator.userAgent);
      formData.append("screen_resolution", `${window.screen.width}x${window.screen.height}`);

      const apiUrl = process.env.REACT_APP_API_URL || "";
      const response = await fetch(`${apiUrl}/api/bug-reports`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to submit test result");
      }

      // Capture report ID for survey
      if (data.reportId) {
        setLastReportId(data.reportId);
      }

      setSubmitSuccess(true);
      setModalStep('complete');

    } catch (error: any) {
      console.error("Test result submission error:", error);
      setSubmitError(error.message || "Failed to submit test result. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Go back to previous step
  const handleBack = () => {
    switch (modalStep) {
      case 'instructions':
        setModalStep('select');
        setSelectedTest(null);
        break;
      case 'running':
        setModalStep('instructions');
        break;
      case 'result':
        setModalStep('running');
        break;
      default:
        break;
    }
  };

  if (!isOpen) return null;

  // Minimized floating button
  if (isMinimized && selectedTest) {
    return (
      <div className="test-runner-minimized" onClick={handleMaximize}>
        <div className="minimized-content">
          <span className="minimized-icon">📋</span>
          <div className="minimized-info">
            <span className="minimized-test-id">{selectedTest.testId}</span>
            <span className="minimized-progress">
              {completedSteps.filter(Boolean).length}/{selectedTest.steps.length} steps
            </span>
          </div>
          <span className="minimized-expand" title="Expand">▲</span>
        </div>
      </div>
    );
  }

  // Success/Complete screen
  if (modalStep === 'complete') {
    return (
      <>
        <div className="test-runner-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
          <div className="test-runner-modal test-runner-success">
            <div className={`test-runner-result-icon ${testResult}`}>
              {testResult === 'pass' ? '✓' : '✗'}
            </div>
            <h2>Test {testResult === 'pass' ? 'Passed' : 'Failed'}!</h2>
            <p>Thank you for testing <strong>{selectedTest?.testId}</strong></p>
            <p className="result-subtitle">Your result has been recorded.</p>
            <div className="complete-actions">
              <button className="btn-survey" onClick={() => setShowSurvey(true)}>
                📋 Take Quick Survey
              </button>
              <button className="btn-close-success" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>

        {/* Survey Modal */}
        <SurveyModal
          isOpen={showSurvey}
          onClose={() => {
            setShowSurvey(false);
            onClose();
          }}
          pageName={selectedTest?.category || 'Unknown'}
          pageUrl={selectedTest?.navigateTo}
          bugReportId={lastReportId || undefined}
        />
      </>
    );
  }

  return (
    <div className="test-runner-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="test-runner-modal">
        {/* Header */}
        <div className="test-runner-header">
          <h2>
            {modalStep === 'select' && 'Run a Test'}
            {modalStep === 'instructions' && 'Test Instructions'}
            {modalStep === 'running' && 'Running Test'}
            {modalStep === 'result' && 'Submit Result'}
          </h2>
          <div className="header-buttons">
            {modalStep === 'running' && (
              <button className="test-runner-minimize" onClick={handleMinimize} title="Minimize to corner">
                ▼
              </button>
            )}
            <button className="test-runner-close" onClick={onClose} title="Close">
              &times;
            </button>
          </div>
        </div>

        {/* Progress indicator */}
        <div className="test-runner-progress">
          <div className={`progress-step ${modalStep === 'select' ? 'active' : (modalStep !== 'select' ? 'completed' : '')}`}>
            <span className="progress-number">1</span>
            <span className="progress-label">Select</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${modalStep === 'instructions' ? 'active' : (['running', 'result'].includes(modalStep) ? 'completed' : '')}`}>
            <span className="progress-number">2</span>
            <span className="progress-label">Review</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${modalStep === 'running' ? 'active' : (modalStep === 'result' ? 'completed' : '')}`}>
            <span className="progress-number">3</span>
            <span className="progress-label">Test</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${modalStep === 'result' ? 'active' : ''}`}>
            <span className="progress-number">4</span>
            <span className="progress-label">Result</span>
          </div>
        </div>

        {/* Error message */}
        {submitError && (
          <div className="test-runner-error">
            {submitError}
          </div>
        )}

        {/* Step 1: Select Test */}
        {modalStep === 'select' && (
          <div className="test-runner-select">
            <p className="step-instruction">Choose a test to run:</p>

            {/* Filters */}
            <div className="test-runner-filters">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">All Categories</option>
                {getCategories().map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>

              <input
                type="text"
                placeholder="Search tests..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="filter-search"
              />
            </div>

            {/* Test list */}
            <div className="test-runner-list">
              {filteredTests.length === 0 ? (
                <div className="no-tests">No tests match your filter</div>
              ) : (
                filteredTests.map(test => (
                  <div
                    key={test.testId}
                    className="test-item"
                    onClick={() => handleTestSelect(test)}
                  >
                    <div className="test-item-header">
                      <span className="test-id">{test.testId}</span>
                      <span className="test-category">{test.category}</span>
                      <span className="test-steps-count">{test.steps.length} steps</span>
                    </div>
                    <div className="test-item-body">
                      <div className="test-description">{test.description}</div>
                      <div className="test-expected">Expected: {test.expectedResult}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Step 2: Instructions */}
        {modalStep === 'instructions' && selectedTest && (
          <div className="test-runner-instructions">
            {/* Test summary */}
            <div className="test-summary-card">
              <div className="summary-header">
                <span className="test-id">{selectedTest.testId}</span>
                <span className="test-category">{selectedTest.category}</span>
              </div>
              <h3>{selectedTest.description}</h3>
              <p className="expected-result">
                <strong>Expected Result:</strong> {selectedTest.expectedResult}
              </p>
              {selectedTest.navigateTo && (
                <p className="navigate-hint">
                  📍 This test is performed on: <strong>{selectedTest.navigateTo}</strong>
                </p>
              )}
            </div>

            {/* Steps preview */}
            <div className="steps-preview">
              <h4>Test Steps ({selectedTest.steps.length})</h4>
              <ol className="steps-list-preview">
                {selectedTest.steps.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>
            </div>

            {/* Actions */}
            <div className="test-runner-actions">
              <button className="btn-back" onClick={handleBack}>
                Back
              </button>
              <button className="btn-start" onClick={handleStartTest}>
                Start Test
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Running Test */}
        {modalStep === 'running' && selectedTest && (
          <div className="test-runner-running">
            {/* Minimize hint */}
            <div className="minimize-hint">
              💡 Click the <strong>▼</strong> button above to minimize this panel while you perform the test steps
            </div>

            {/* Current test info */}
            <div className="running-test-info">
              <span className="test-id">{selectedTest.testId}</span>
              <span className="test-description">{selectedTest.description}</span>
            </div>

            {/* Steps checklist */}
            <div className="steps-checklist">
              <h4>Complete each step:</h4>
              {selectedTest.steps.map((step, index) => (
                <div
                  key={index}
                  className={`step-item ${completedSteps[index] ? 'completed' : ''} ${currentStepIndex === index ? 'current' : ''}`}
                >
                  <label className="step-checkbox">
                    <input
                      type="checkbox"
                      checked={completedSteps[index]}
                      onChange={() => handleStepToggle(index)}
                    />
                    <span className="checkmark"></span>
                  </label>
                  <div className="step-content">
                    <span className="step-number">Step {index + 1}</span>
                    <span className="step-text">{step}</span>
                  </div>
                  {!completedSteps[index] && currentStepIndex === index && (
                    <button
                      className="btn-step-done"
                      onClick={() => handleStepComplete(index)}
                    >
                      Done
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Progress */}
            <div className="steps-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${(completedSteps.filter(Boolean).length / selectedTest.steps.length) * 100}%` }}
                ></div>
              </div>
              <span className="progress-text">
                {completedSteps.filter(Boolean).length} / {selectedTest.steps.length} steps completed
              </span>
            </div>

            {/* Actions */}
            <div className="test-runner-actions">
              <button className="btn-back" onClick={handleBack}>
                Back
              </button>
              <button
                className="btn-finish"
                onClick={handleFinishTest}
              >
                Finish & Report Result
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Submit Result */}
        {modalStep === 'result' && selectedTest && (
          <div className="test-runner-result">
            {/* Test summary */}
            <div className="result-test-summary">
              <span className="test-id">{selectedTest.testId}</span>
              <span className="test-description">{selectedTest.description}</span>
              <span className="steps-completed">
                {completedSteps.filter(Boolean).length}/{selectedTest.steps.length} steps completed
              </span>
            </div>

            {/* Pass/Fail selection */}
            <div className="result-selection">
              <h4>Did the test pass or fail?</h4>
              <div className="result-buttons">
                <button
                  className={`btn-result btn-pass ${testResult === 'pass' ? 'selected' : ''}`}
                  onClick={() => setTestResult('pass')}
                >
                  <span className="result-icon">✓</span>
                  <span>PASS</span>
                  <span className="result-hint">Test worked as expected</span>
                </button>
                <button
                  className={`btn-result btn-fail ${testResult === 'fail' ? 'selected' : ''}`}
                  onClick={() => setTestResult('fail')}
                >
                  <span className="result-icon">✗</span>
                  <span>FAIL</span>
                  <span className="result-hint">Something didn't work right</span>
                </button>
              </div>
            </div>

            {/* Comments */}
            <div className="result-comments">
              <label htmlFor="comments">
                Additional Comments {testResult === 'fail' && <span className="required">*</span>}
              </label>
              <textarea
                id="comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder={testResult === 'fail'
                  ? "Please describe what went wrong..."
                  : "Any additional notes (optional)..."
                }
                rows={3}
              />
            </div>

            {/* Actions */}
            <div className="test-runner-actions">
              <button className="btn-back" onClick={handleBack} disabled={isSubmitting}>
                Back
              </button>
              <button
                className="btn-submit"
                onClick={handleSubmitResult}
                disabled={!testResult || isSubmitting || (testResult === 'fail' && !comments.trim())}
              >
                {isSubmitting ? "Submitting..." : "Submit Result"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestRunnerModal;
